(function () {
  const API_URL =
    (window.EveryTable && window.EveryTable.api && window.EveryTable.api.baseUrl) ||
    window.ET_ADMIN_API_URL ||
    'https://admin-app.everytable-sh.com/api';

  let cachedAuthToken = null;
  let authTokenExpiry = null;

  function parseErrorMessage(err) {
    var raw =
      (err && err.message) ||
      (err && err.error && typeof err.error === 'string' ? err.error : null) ||
      '';
    if (!raw || typeof raw !== 'string') return raw || 'Something went wrong. Please try again.';
    var trimmed = raw.trim();
    if (trimmed.charAt(0) !== '{' && trimmed.charAt(0) !== '[') return raw;
    try {
      var parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object') {
        var discount = parsed.discount;
        if (Array.isArray(discount) && discount.length > 0 && typeof discount[0] === 'string') {
          return discount[0];
        }
        var error = parsed.error;
        if (typeof error === 'string') return error;
        if (Array.isArray(error) && error.length > 0 && typeof error[0] === 'string') return error[0];
        var msg = parsed.message;
        if (typeof msg === 'string') return msg;
      }
    } catch (e) {}
    return raw;
  }

  function callToastIfAvailable(fn, message) {
    if (typeof fn === 'function') {
      try {
        fn(message);
      } catch (e) {
        console.warn('[Loyalty] Toast call failed', e, message);
      }
    } else {
      console.warn('[Loyalty] Toast function unavailable', { message });
    }
  }

  function escapeHtml(value) {
    let sharedEscape = window.etRechargeUtils && window.etRechargeUtils.escapeHtml;
    if (typeof sharedEscape === 'function') {
      return sharedEscape(value);
    }
    if (value === null || value === undefined) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function isVoidedClaimedReward(reward) {
    if (!reward || typeof reward !== 'object') return false;
    let state = reward.state;
    if (state == null && reward.redeemable && typeof reward.redeemable === 'object') {
      state = reward.redeemable.state;
    }
    return typeof state === 'string' && state.trim().toLowerCase() === 'void';
  }

  function getSharedRechargeSdk() {
    let sharedGetter =
      window.etRechargeUtils && window.etRechargeUtils.getSharedRechargeSdk;
    if (typeof sharedGetter === 'function') {
      return sharedGetter();
    }
    const sharedSdk = window.etRechargeSdk;
    if (
      !sharedSdk ||
      typeof sharedSdk.ready !== 'function' ||
      typeof sharedSdk.getMethod !== 'function'
    ) {
      throw new Error(
        'Shared Recharge SDK is unavailable. Ensure recharge-sdk-bootstrap.js loads before affinity-loyalty-redeem.js.'
      );
    }
    return sharedSdk;
  }

  function getRechargeMethod(pathSegments) {
    let sharedGetter =
      window.etRechargeUtils && window.etRechargeUtils.getRechargeMethod;
    if (typeof sharedGetter === 'function') {
      return sharedGetter(pathSegments);
    }
    return getSharedRechargeSdk().getMethod(pathSegments);
  }

  // Reads share one per-page-view memo with every other portal script, so
  // "Apply rewards" reuses what the overview already fetched (#341).
  function rechargeRead(pathSegments, session, params) {
    let sharedRead =
      window.etRechargeUtils && window.etRechargeUtils.memoizedRechargeRead;
    if (typeof sharedRead === 'function') {
      return sharedRead(pathSegments, session, params);
    }
    return getRechargeMethod(pathSegments)(session, params);
  }

  // Writes invalidate that memo before the caller's `await` resumes.
  function rechargeWrite(pathSegments) {
    let args = Array.prototype.slice.call(arguments, 1);
    let sharedWrite =
      window.etRechargeUtils && window.etRechargeUtils.runRechargeWrite;
    if (typeof sharedWrite === 'function') {
      return sharedWrite.apply(null, [pathSegments].concat(args));
    }
    return getRechargeMethod(pathSegments).apply(null, args);
  }

  function resolveChargeDateValue(charge, subscription) {
    let sharedResolver =
      window.etRechargeUtils && window.etRechargeUtils.resolveChargeDateValue;
    if (typeof sharedResolver === 'function') {
      return sharedResolver(charge, subscription);
    }
    let candidate = charge && (charge.scheduled_at || charge.charge_date || charge.created_at);
    if (!candidate && subscription) {
      candidate =
        subscription.next_charge_date ||
        subscription.next_charge_scheduled_at ||
        subscription.next_charge_at;
    }
    let parsed = new Date(candidate || '');
    return Number.isFinite(parsed.getTime()) ? parsed : null;
  }

  // One mint at a time: the idle rewards prefetch (#363) can overlap a click's
  // own loyalty call, and each caller would otherwise mint its own token.
  var authTokenPromise = null;

  function getAuthToken() {
    if (cachedAuthToken && authTokenExpiry && Date.now() < authTokenExpiry) {
      return Promise.resolve(cachedAuthToken);
    }
    if (!authTokenPromise) {
      authTokenPromise = mintAuthToken().finally(function () {
        authTokenPromise = null;
      });
    }
    return authTokenPromise;
  }

  async function mintAuthToken() {
    try {
      const session = await getRechargeSession();
      if (!session || !session.apiToken || !session.customerId) {
        throw new Error('No Recharge portal session for auth');
      }

      // The Recharge portal session is the only proof the admin app accepts;
      // it reads the customer from Recharge (everytable-shopify-admin-app#434).
      const authResponse = await fetch(API_URL + '/subscription/auth/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rechargeCustomerId: session.customerId,
          rechargeApiToken: session.apiToken
        })
      });

      if (!authResponse.ok) {
        const errorData = await authResponse.json().catch(function () {
          return {};
        });
        throw new Error(
          'Auth failed: ' + authResponse.status + ' - ' + (errorData.error || 'Unknown error')
        );
      }

      const authData = await authResponse.json();
      cachedAuthToken = authData.token;
      authTokenExpiry = Date.now() + ((authData.expiresIn || 3600) - 60) * 1000;

      return cachedAuthToken;
    } catch (error) {
      console.error('[Loyalty] Failed to get auth token:', error);
      throw error;
    }
  }

  async function callAPI(endpoint, options) {
    if (options === void 0) {
      options = {};
    }
    try {
      let headers = { 'Content-Type': 'application/json' };

      try {
        let token = await getAuthToken();
        if (token) {
          headers.Authorization = 'Bearer ' + token;
        }
      } catch (authError) {
        let rechargeToken =
          window.ReCharge && window.ReCharge.session && window.ReCharge.session.token;
        if (rechargeToken) {
          headers.Authorization = 'Bearer ' + rechargeToken;
        }
      }

      let response = await fetch(API_URL + endpoint, {
        method: options.method || 'POST',
        headers: headers,
        body: options.body ? JSON.stringify(options.body) : void 0
      });

      let contentType = response.headers.get('content-type') || '';

      if (contentType.indexOf('application/json') === -1) {
        let text = await response.text();
        console.error('[Loyalty] Non-JSON response:', text);
        throw new Error('Invalid response from loyalty API');
      }

      let data = await response.json();

      if (!response.ok) {
        throw new Error((data && data.error) || 'API error ' + response.status);
      }

      return data;
    } catch (error) {
      console.error('[Loyalty] API call failed:', error, error && error.message, error && error.response);
      throw error;
    }
  }

  let LOYALTY_PURCHASE_TYPE = 'all';

  async function getRechargeSession() {
    try {
      let sharedGetPortalSession =
        window.etRechargeUtils && window.etRechargeUtils.getPortalSession;
      let session =
        typeof sharedGetPortalSession === 'function'
          ? await sharedGetPortalSession()
          : await getRechargeMethod(['auth', 'loginCustomerPortal'])();
      return session;
    } catch (error) {
      console.error('[Loyalty] Failed to get Recharge session:', error);
      throw error;
    }
  }

  async function loadSubscriptions() {
    try {
      let session = await getRechargeSession();

      // Same params the overview asks with, so both hit one memo entry; the
      // id-asc order this list used to request from Recharge is applied here.
      const [subscriptionsResponse, chargesResponse] = await Promise.all([
        rechargeRead(['subscription', 'listSubscriptions'], session, { limit: 250 }),
        rechargeRead(['charge', 'listCharges'], session, {
          status: 'queued',
          limit: 250,
          sort_by: 'scheduled_at-asc'
        })
      ]);

      let subscriptions = (
        Array.isArray(subscriptionsResponse)
          ? subscriptionsResponse
          : (subscriptionsResponse && subscriptionsResponse.subscriptions) || []
      ).slice().sort(function (a, b) {
        return Number(a && a.id) - Number(b && b.id);
      });

      let charges = Array.isArray(chargesResponse)
        ? chargesResponse
        : (chargesResponse && chargesResponse.charges) || [];

      let subscriptionsWithCharges = subscriptions.map(function (sub) {
        let nextCharge = null;

        if (sub.address_id) {
          let candidateCharges = charges.filter(function (charge) {
            if (String(charge.address_id) !== String(sub.address_id)) return false;
            if (Array.isArray(charge.line_items)) {
              return charge.line_items.some(function (li) {
                return String(li.purchase_item_id) === String(sub.id);
              });
            }
            return false;
          });

          if (candidateCharges.length > 0) {
            candidateCharges.sort(function (a, b) {
              let daResolved = resolveChargeDateValue(a, sub);
              let dbResolved = resolveChargeDateValue(b, sub);
              let da = daResolved ? daResolved.getTime() : Number.MAX_SAFE_INTEGER;
              let db = dbResolved ? dbResolved.getTime() : Number.MAX_SAFE_INTEGER;
              return da - db;
            });
            nextCharge = candidateCharges[0];
          }
        }

        if (!nextCharge) {
          let subCharges = charges.filter(function (charge) {
            return String(charge.subscription_id) === String(sub.id);
          });
          if (subCharges.length > 0) {
            subCharges.sort(function (a, b) {
              let daResolved = resolveChargeDateValue(a, sub);
              let dbResolved = resolveChargeDateValue(b, sub);
              let da = daResolved ? daResolved.getTime() : Number.MAX_SAFE_INTEGER;
              let db = dbResolved ? dbResolved.getTime() : Number.MAX_SAFE_INTEGER;
              return da - db;
            });
            nextCharge = subCharges[0];
          }
        }

        let discountCodes = [];
        if (nextCharge && nextCharge.discount_code && typeof nextCharge.discount_code === 'string') {
          discountCodes.push(nextCharge.discount_code.trim());
        }
        if (nextCharge && Array.isArray(nextCharge.discounts)) {
          nextCharge.discounts.forEach(function (d) {
            if (d && d.code && typeof d.code === 'string') {
              discountCodes.push(d.code.trim());
            }
          });
        }

        let hasNextChargeDiscount =
          nextCharge &&
          ((nextCharge.discount_code && nextCharge.discount_code.trim() !== '') ||
            (nextCharge.total_discounts && parseFloat(nextCharge.total_discounts) > 0) ||
            (nextCharge.discount_id && nextCharge.discount_id !== null) ||
            discountCodes.length > 0);

        let subLineItems =
          nextCharge && Array.isArray(nextCharge.line_items)
            ? nextCharge.line_items.filter(function (li) {
                return String(li.purchase_item_id) === String(sub.id);
              })
            : [];

        return {
          id: sub.id,
          status: sub.status,
          title: sub.product_title || 'Subscription #' + sub.id,
          nextChargeAt: (function () {
            let resolvedDate = resolveChargeDateValue(nextCharge, sub);
            return resolvedDate ? resolvedDate.toISOString() : null;
          })(),
          hasNextChargeDiscount: Boolean(hasNextChargeDiscount),
          currentDiscountCode: discountCodes.length > 0 ? discountCodes[0] : null,
          discountCodes: discountCodes,
          next_charge_id: nextCharge ? nextCharge.id : null,
          line_items: subLineItems,
          address_id: sub.address_id,
          total_discounts:
            nextCharge && (nextCharge.total_discounts != null || nextCharge.discount_total != null)
              ? parseFloat(
                  nextCharge.total_discounts != null
                    ? nextCharge.total_discounts
                    : nextCharge.discount_total || 0
                ) || 0
              : 0
        };
      });

      return { subscriptions: subscriptionsWithCharges };
    } catch (error) {
      console.error('[Loyalty] Failed to load subscriptions via Recharge SDK:', error);
      throw error;
    }
  }

  async function loadLoyaltyRewards(purchaseType) {
    const type = purchaseType != null ? purchaseType : LOYALTY_PURCHASE_TYPE;

    return callAPI('/loyalty/rewards', {
      body: { purchase_type: '' }
    });
  }

  // The rewards list the overview warmed while idle (#363), held for the next
  // "Apply rewards" open and handed to it once. It is customer data, so it is
  // only good for a while, and any write or refresh drops it. The auth token
  // it minted stays in memory as before.
  var REWARDS_PREFETCH_MAX_AGE_MS = 5 * 60 * 1000;
  // How long an open joins a prefetch still in flight; past it the request
  // has likely stalled (a tab that slept), so the open asks again.
  var REWARDS_PREFETCH_JOIN_MS = 10000;
  var prefetchedRewards = null;

  function isUsablePrefetchedRewards(entry) {
    if (!entry) return false;
    const now = Date.now();
    if (entry.fetchedAt === null) return now - entry.startedAt < REWARDS_PREFETCH_JOIN_MS;
    return now - entry.fetchedAt < REWARDS_PREFETCH_MAX_AGE_MS;
  }

  function prefetchLoyaltyRewards() {
    // An open already loading asks for the list itself.
    if (isLoyaltySubscriptionsLoading) return;
    if (isUsablePrefetchedRewards(prefetchedRewards)) return;
    const entry = { promise: loadLoyaltyRewards(), startedAt: Date.now(), fetchedAt: null };
    entry.promise.then(
      function () {
        entry.fetchedAt = Date.now();
      },
      function () {
        // callAPI has logged it; the click asks again.
        forgetPrefetchedRewards(entry);
      }
    );
    prefetchedRewards = entry;
  }

  function forgetPrefetchedRewards(entry) {
    if (!entry || prefetchedRewards === entry) prefetchedRewards = null;
  }

  function takeLoyaltyRewards() {
    const entry = prefetchedRewards;
    prefetchedRewards = null;
    return isUsablePrefetchedRewards(entry) ? entry.promise : loadLoyaltyRewards();
  }

  async function redeemReward(reward) {
    forgetPrefetchedRewards();
    return callAPI('/loyalty/redeem', {
      body: {
        reward_id: reward.id,
        reward_type: reward.reward_type || null
      }
    });
  }

  async function applyVoucherToSubscription(subscriptionId, voucherCode, nextChargeId) {
    try {
      let session = await getRechargeSession();
      let subsResponse = await loadSubscriptions();
      let sub =
        subsResponse &&
        subsResponse.subscriptions &&
        subsResponse.subscriptions.find(function (s) {
          return s.id === subscriptionId;
        });

      if (!sub || !sub.address_id) {
        throw new Error('No address found for this subscription');
      }

      await rechargeWrite(
        ['address', 'applyDiscountToAddress'],
        session,
        sub.address_id,
        voucherCode
      );

      return {
        success: true,
        voucher_code: voucherCode,
        applied_to_address_id: sub.address_id
      };
    } catch (error) {
      console.error('[Loyalty] Failed to apply voucher via Recharge SDK:', error);
      throw error;
    }
  }

  let modalEl = null;
  let rewardsContainerEl = null;
  let claimedContainerEl = null;
  let summaryEl = null;
  let promoInputEl = null;
  let promoApplyBtnEl = null;
  let promoErrorEl = null;
  let latestSubscriptions = [];
  let selectedSubscriptionId = null;
  let isLoyaltySubscriptionsLoading = false;
  let releaseFocusTrap = null;
  let previousFocusedElement = null;
  const ACTIVE_TAB_CLASS = 'et-loyalty-tab--active';
  const ACTIVE_PANEL_CLASS = 'et-loyalty-panel--active';

  function createModal() {
    if (modalEl) return;

    modalEl = document.createElement('div');
    modalEl.id = 'et-loyalty-modal';

    modalEl.innerHTML =
      '' +
      '<div class="et-loyalty-modal-container">' +
      '<button id="et-loyalty-close">\u00D7</button>' +
      '<h3>Redeem Rewards</h3>' +
      '<div class="et-loyalty-tabs" role="tablist">' +
      '<button type="button" class="et-loyalty-tab ' + ACTIVE_TAB_CLASS + '" data-tab="loyalty" role="tab" aria-selected="true">Loyalty</button>' +
      '<button type="button" class="et-loyalty-tab" data-tab="promo" role="tab" aria-selected="false">Promo Code</button>' +
      '</div>' +
      '<div id="et-loyalty-panel-loyalty" class="et-loyalty-panel ' + ACTIVE_PANEL_CLASS + '" role="tabpanel">' +
      '<div id="et-loyalty-summary" style="margin-bottom:12px;font-size:14px;color:#444;"></div>' +
      '<div id="et-loyalty-claimed"></div>' +
      '<hr id="et-loyalty-divider" style="margin:16px 0;border:none;border-top:1px solid #eee;" />' +
      '<div id="et-loyalty-rewards"></div>' +
      '</div>' +
      '<div id="et-loyalty-panel-promo" class="et-loyalty-panel" role="tabpanel" hidden>' +
      '<div class="et-loyalty-promo-form" style="display:flex;flex-direction:column;gap:12px;margin-top:8px;">' +
      '<label for="et-loyalty-promo-input" style="font-size:14px;font-weight:500;">Enter promo code</label>' +
      '<input type="text" id="et-loyalty-promo-input" class="et-loyalty-promo-input" placeholder="e.g. SAVE10" autocomplete="off" style="padding:10px 12px;border:1px solid #ddd;border-radius:6px;font-size:14px;" />' +
      '<div id="et-loyalty-promo-error" style="display:none;color:#b00020;font-size:13px;"></div>' +
      '<button type="button" id="et-loyalty-promo-apply" data-promo-apply style="padding:10px 16px;border-radius:6px;border:none;cursor:pointer;background:#0d3c3a;color:#fff;font-size:14px;align-self:flex-start;">Apply</button>' +
      '</div>' +
      '</div>' +
      '</div>';

    document.body.appendChild(modalEl);

    rewardsContainerEl = modalEl.querySelector('#et-loyalty-rewards');
    claimedContainerEl = modalEl.querySelector('#et-loyalty-claimed');
    summaryEl = modalEl.querySelector('#et-loyalty-summary');
    promoInputEl = modalEl.querySelector('#et-loyalty-promo-input');
    promoApplyBtnEl = modalEl.querySelector('#et-loyalty-promo-apply');
    promoErrorEl = modalEl.querySelector('#et-loyalty-promo-error');

    modalEl.querySelector('#et-loyalty-close').addEventListener('click', closeModal);

    modalEl.addEventListener('click', function (e) {
      if (e.target === modalEl) closeModal();
    });

    modalEl.querySelectorAll('.et-loyalty-tab').forEach(function (tab) {
      tab.addEventListener('click', function () {
        switchTab(tab.getAttribute('data-tab'));
      });
    });

    if (promoApplyBtnEl) {
      promoApplyBtnEl.addEventListener('click', handleApplyPromoCode);
    }
    if (promoInputEl) {
      promoInputEl.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleApplyPromoCode();
        }
      });
    }
  }

  function setPromoError(message) {
    if (!promoErrorEl) return;
    const text = (message == null ? '' : String(message)).trim();
    if (!text) {
      promoErrorEl.textContent = '';
      promoErrorEl.style.display = 'none';
      return;
    }
    promoErrorEl.textContent = text;
    promoErrorEl.style.display = 'block';
  }

  function switchTab(tabId) {
    if (!modalEl) return;
    var tabs = modalEl.querySelectorAll('.et-loyalty-tab');
    var panels = modalEl.querySelectorAll('.et-loyalty-panel');
    tabs.forEach(function (t) {
      var isActive = t.getAttribute('data-tab') === tabId;
      t.classList.toggle(ACTIVE_TAB_CLASS, isActive);
      t.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    panels.forEach(function (p) {
      var panelId = p.id;
      var isActive = (panelId === 'et-loyalty-panel-loyalty' && tabId === 'loyalty') ||
        (panelId === 'et-loyalty-panel-promo' && tabId === 'promo');
      p.classList.toggle(ACTIVE_PANEL_CLASS, isActive);
      p.hidden = !isActive;
      if (isActive && p.id === 'et-loyalty-panel-promo' && promoInputEl) {
        promoInputEl.focus();
      }
    });
  }

  function openModal() {
    createModal();
    switchTab('loyalty');
    if (promoInputEl) promoInputEl.value = '';
    setPromoError('');
    if (promoApplyBtnEl) {
      promoApplyBtnEl.disabled = false;
      promoApplyBtnEl.textContent = 'Apply';
      promoApplyBtnEl.style.background = '#0d3c3a';
    }
    previousFocusedElement = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    modalEl.style.display = 'flex';
    let modalContainer = modalEl.querySelector('.et-loyalty-modal-container');
    let trapFocus = window.etRechargeUtils && window.etRechargeUtils.trapFocus;
    if (typeof trapFocus === 'function' && modalContainer instanceof HTMLElement) {
      releaseFocusTrap = trapFocus(modalContainer, {
        onEscape: closeModal,
      });
    }
  }

  function closeModal() {
    if (typeof releaseFocusTrap === 'function') {
      releaseFocusTrap();
      releaseFocusTrap = null;
    }
    if (modalEl) modalEl.style.display = 'none';
    selectedSubscriptionId = null;
    if (
      previousFocusedElement instanceof HTMLElement &&
      document.contains(previousFocusedElement)
    ) {
      previousFocusedElement.focus();
      previousFocusedElement = null;
    }
  }

  async function showRewardsForSubscription(subscriptionId) {
    rewardsContainerEl.innerHTML = 'Loading rewards…';

    try {
      // Independent reads, so neither waits on the other.
      const [subsResponse, rewardsResponse] = await Promise.all([
        loadSubscriptions(),
        takeLoyaltyRewards(),
      ]);
      latestSubscriptions = (subsResponse && subsResponse.subscriptions) || [];

      let allRewards = (rewardsResponse && rewardsResponse.rewards) || [];
      let customer = rewardsResponse && rewardsResponse.customer;
      let allClaimed = (rewardsResponse && rewardsResponse.claimed_rewards) || [];

      const preferredType = 'active_subscription_discount_voucher';
      const fallbackType = 'cart_discount_voucher';

      let rewards = Array.isArray(allRewards)
        ? allRewards.filter(function (reward) {
            return reward && reward.reward_type === preferredType;
          })
        : [];
      if (!rewards.length && Array.isArray(allRewards)) {
        rewards = allRewards.filter(function (reward) {
          return reward && reward.reward_type === fallbackType;
        });
      }

      let claimed = Array.isArray(allClaimed)
        ? allClaimed.filter(function (r) {
            return r && r.redeemable && r.redeemable.kind === preferredType && !isVoidedClaimedReward(r);
          })
        : [];
      if (!claimed.length && Array.isArray(allClaimed)) {
        claimed = allClaimed.filter(function (r) {
          return r && r.redeemable && r.redeemable.kind === fallbackType && !isVoidedClaimedReward(r);
        });
      }

      if (summaryEl && customer && customer.points) {
        let current =
          customer.points.current != null
            ? customer.points.current
            : customer.points_approved != null
              ? customer.points_approved
              : 0;
        let pending =
          customer.points.pending != null
            ? customer.points.pending
            : customer.points_pending != null
              ? customer.points_pending
              : 0;
        summaryEl.textContent = 'Points: ' + current + ' available, ' + pending + ' pending';
      }

      renderRewards(rewards, subscriptionId);
      renderClaimedRewards(claimed, subscriptionId);
    } catch (err) {
      console.error('[Loyalty] Failed to load rewards', err);
      rewardsContainerEl.innerHTML =
        '<p>You don&rsquo;t have any subscription rewards available right now.</p>';
    }
  }

  function renderRewards(rewards, subscriptionId) {
    if (rewards === void 0) {
      rewards = [];
    }
    if (!Array.isArray(rewards) || rewards.length === 0) {
      rewardsContainerEl.innerHTML = '<p>No subscription rewards available.</p>';
      return;
    }

    rewardsContainerEl.innerHTML =
      rewards
        .map(function (reward) {
          let nameHtml = escapeHtml(reward.name);
          let descriptionHtml = escapeHtml(reward.description || '');
          let costHtml =
            reward.cost_text != null
              ? escapeHtml(reward.cost_text)
              : escapeHtml((reward.point_cost || reward.points_required || 0) + ' points');

          return (
            '' +
            '<div style="border:1px solid #eee;padding:12px;margin-bottom:10px;border-radius:6px;display:flex;justify-content:space-between;align-items:center;gap:12px;">' +
            '<div style="flex:1;">' +
            '<strong>' +
            nameHtml +
            '</strong>' +
            '<div style="font-size:13px;color:#555;margin-top:4px;">' +
            '<div>' +
            descriptionHtml +
            '</div>' +
            '<div style="margin-top:4px;font-weight:500;">' +
            costHtml +
            '</div>' +
            '<div style="margin-top:2px;font-size:12px;color:' +
            (reward.can_redeem ? '#0a7c3a' : '#a00') +
            ';">' +
            '</div>' +
            '</div>' +
            '</div>' +
            '<button' +
            ' data-reward-id="' +
            escapeHtml(String(reward.id)) +
            '"' +
            ' style="padding:6px 12px;border-radius:4px;border:none;cursor:' +
            (reward.can_redeem ? 'pointer' : 'not-allowed') +
            ';background:' +
            (reward.can_redeem ? '#0d3c3a' : '#ccc') +
            ';color:#fff;white-space:nowrap;"' +
            (reward.can_redeem ? '' : ' disabled') +
            '>' +
            'Redeem' +
            '</button>' +
            '</div>'
          );
        })
        .join('');

    rewardsContainerEl.querySelectorAll('button[data-reward-id]').forEach(function (btn) {
      btn.addEventListener('click', async function () {
        let rewardId = Number(btn.getAttribute('data-reward-id'));
        let reward = rewards.find(function (r) {
          return Number(r.id) === rewardId;
        });
        if (!reward || !reward.can_redeem) return;

        await handleRedeemReward(reward, subscriptionId, btn);
      });
    });
  }

  function disableOtherLoyaltyActionButtons(activeButtonEl) {
    if (!modalEl) return [];
    let allButtons = modalEl.querySelectorAll(
      'button[data-reward-id], button[data-apply-code], button[data-promo-apply]'
    );
    let temporarilyDisabled = [];
    allButtons.forEach(function (btn) {
      if (btn === activeButtonEl) return;
      if (!btn.disabled) {
        btn.dataset.etPrevBg = btn.style.background || '';
        btn.dataset.etPrevCursor = btn.style.cursor || '';
        btn.dataset.etPrevOpacity = btn.style.opacity || '';
        btn.disabled = true;
        btn.style.background = '#ccc';
        btn.style.cursor = 'not-allowed';
        btn.style.opacity = '0.6';
        temporarilyDisabled.push(btn);
      }
    });
    return temporarilyDisabled;
  }

  function restoreLoyaltyActionButtons(buttons) {
    if (!buttons || !buttons.length) return;
    buttons.forEach(function (btn) {
      if (!btn) return;
      btn.disabled = false;
      if (btn.dataset.etPrevBg !== undefined) {
        btn.style.background = btn.dataset.etPrevBg;
        delete btn.dataset.etPrevBg;
      }
      if (btn.dataset.etPrevCursor !== undefined) {
        btn.style.cursor = btn.dataset.etPrevCursor;
        delete btn.dataset.etPrevCursor;
      }
      if (btn.dataset.etPrevOpacity !== undefined) {
        btn.style.opacity = btn.dataset.etPrevOpacity;
        delete btn.dataset.etPrevOpacity;
      }
    });
  }

  async function handleRedeemReward(reward, subscriptionId, buttonEl) {
    let temporarilyDisabled = disableOtherLoyaltyActionButtons(buttonEl);
    buttonEl.disabled = true;
    buttonEl.textContent = 'Checking…';

    try {
      let subsResponse = await loadSubscriptions();
      let subscription =
        subsResponse &&
        subsResponse.subscriptions &&
        subsResponse.subscriptions.find(function (sub) {
          return sub.id === subscriptionId;
        });

      if (!subscription) {
        throw new Error('Subscription not found');
      }

      if (subscription.hasNextChargeDiscount) {
        restoreLoyaltyActionButtons(temporarilyDisabled);
        buttonEl.disabled = false;
        buttonEl.textContent = 'Redeem';
        callToastIfAvailable(
          window.showWarningToast,
          'This subscription already has a discount on its next charge. Please select a different subscription.'
        );
        return;
      }

      buttonEl.textContent = 'Redeeming…';

      let redeemResult = await redeemReward(reward);

      if (!redeemResult || !redeemResult.success || !redeemResult.voucher_code) {
        throw new Error('Invalid redeem response');
      }

      buttonEl.textContent = 'Applying…';

      let applyResult = await applyVoucherToSubscription(
        subscriptionId,
        redeemResult.voucher_code,
        subscription.next_charge_id
      );

      if (applyResult && applyResult.success) {
        restoreLoyaltyActionButtons(temporarilyDisabled);
        buttonEl.textContent = 'Applied ✓';
        buttonEl.style.background = '#0a7c3a';
        let safeRewardName = escapeHtml(String(reward.name));
        let successMessage =
          'Success! Your reward "' +
          safeRewardName +
          '" has been applied to your subscription\'s next charge.';
        callToastIfAvailable(window.showSuccessToast, successMessage);

        applyOverviewDiscountOptimistic(redeemResult.voucher_code);
        closeModal();
        document.dispatchEvent(new CustomEvent('Affinity:refresh'));
      } else {
        throw new Error('Failed to apply voucher');
      }
    } catch (err) {
      console.error('[Loyalty] Redeem/apply failed', err);
      restoreLoyaltyActionButtons(temporarilyDisabled);
      buttonEl.disabled = false;
      buttonEl.textContent = 'Redeem';

      let errorMsg = parseErrorMessage(err) || 'Failed to redeem and apply reward';

      if (errorMsg.indexOf('already has a discount') !== -1) {
        callToastIfAvailable(
          window.showWarningToast,
          'This subscription already has a discount on its next charge. The reward was not redeemed.'
        );
      } else {
        callToastIfAvailable(window.showErrorToast, 'Error: ' + errorMsg);
      }
    }
  }

  function getRewardCode(r) {
    return (
      (r.redeemable && (r.redeemable.code || r.redeemable.voucher_code)) ||
      r.voucher_code ||
      r.code ||
      ''
    )
      .toString()
      .trim()
      .toUpperCase();
  }

  function applyOverviewDiscountOptimistic(voucherCode, amount) {
    let bridge = window.etUpdateMealsExtensions;
    if (!bridge || typeof bridge.applyOverviewDiscountOptimistic !== 'function') {
      return;
    }
    let code = String(voucherCode || '').trim().toUpperCase();
    if (!code) return;
    var numericAmount = Number(amount);
    if (!isFinite(numericAmount)) {
      numericAmount = 0;
    }
    bridge.applyOverviewDiscountOptimistic({ code: code, amount: numericAmount });
  }

  async function updateOverviewDiscountFromServer(subscriptionId, voucherCode) {
    try {
      let subsResponse = await loadSubscriptions();
      let subs = (subsResponse && subsResponse.subscriptions) || [];
      let subscription =
        subs &&
        subs.find(function (sub) {
          return sub.id === subscriptionId;
        });
      let amount =
        subscription && typeof subscription.total_discounts === 'number'
          ? subscription.total_discounts
          : 0;
      applyOverviewDiscountOptimistic(voucherCode, amount);
    } catch (error) {
      console.error(
        '[Loyalty] Failed to refresh overview discount after applying voucher',
        error
      );
      applyOverviewDiscountOptimistic(voucherCode, 0);
    }
  }

  async function handleApplyPromoCode() {
    if (!selectedSubscriptionId || !promoInputEl || !promoApplyBtnEl) return;
    setPromoError('');
    var code = (promoInputEl.value || '').trim();
    if (!code) {
      setPromoError('Please enter a promo code.');
      promoInputEl.focus();
      return;
    }
    const success = await handleApplyUnusedReward(selectedSubscriptionId, code, promoApplyBtnEl);
    if (success && promoInputEl) {
      promoInputEl.value = '';
    }
  }

  async function handleApplyUnusedReward(subscriptionId, code, buttonEl) {
    if (buttonEl && buttonEl.hasAttribute('data-promo-apply')) {
      setPromoError('');
    }
    let temporarilyDisabled = disableOtherLoyaltyActionButtons(buttonEl);
    buttonEl.disabled = true;
    buttonEl.textContent = 'Applying…';
    try {
      let subsResponse = await loadSubscriptions();
      let subscription =
        subsResponse &&
        subsResponse.subscriptions &&
        subsResponse.subscriptions.find(function (sub) {
          return sub.id === subscriptionId;
        });
      if (subscription && subscription.hasNextChargeDiscount) {
        restoreLoyaltyActionButtons(temporarilyDisabled);
        buttonEl.disabled = false;
        buttonEl.textContent = buttonEl.hasAttribute('data-promo-apply') ? 'Apply' : 'Apply';
        callToastIfAvailable(
          window.showWarningToast,
          'This subscription already has a discount on its next charge. Please select a different subscription.'
        );
        return false;
      }
      let applyResult = await applyVoucherToSubscription(subscriptionId, code, null);
      if (applyResult && applyResult.success) {
        restoreLoyaltyActionButtons(temporarilyDisabled);
        buttonEl.textContent = 'Applied ✓';
        buttonEl.style.background = '#0a7c3a';
        callToastIfAvailable(window.showSuccessToast, 'Discount applied to upcoming order.');
        await updateOverviewDiscountFromServer(subscriptionId, code);
        closeModal();
        document.dispatchEvent(new CustomEvent('Affinity:refresh'));
        return true;
      } else {
        throw new Error('Failed to apply');
      }
    } catch (err) {
      console.error('[Loyalty] Apply unused reward failed', err);
      restoreLoyaltyActionButtons(temporarilyDisabled);
      buttonEl.disabled = false;
      buttonEl.textContent = 'Apply';
      const message =
        parseErrorMessage(err) || 'Failed to apply discount. Please try again.';
      if (buttonEl && buttonEl.hasAttribute('data-promo-apply')) {
        setPromoError(message);
      } else {
        callToastIfAvailable(window.showErrorToast, 'Error: ' + message);
      }
    }
    return false;
  }

  function renderClaimedRewards(claimed, subscriptionId) {
    if (claimed === void 0) {
      claimed = [];
    }
    if (!subscriptionId) {
      claimedContainerEl.innerHTML = '';
      return;
    }
    let appliedCodes = new Set();
    if (Array.isArray(latestSubscriptions)) {
      latestSubscriptions.forEach(function (sub) {
        if (!sub) return;
        if (sub.currentDiscountCode) {
          appliedCodes.add(String(sub.currentDiscountCode).trim().toUpperCase());
        }
        if (Array.isArray(sub.discountCodes)) {
          sub.discountCodes.forEach(function (code) {
            if (code) {
              appliedCodes.add(String(code).trim().toUpperCase());
            }
          });
        }
      });
    }

    let unused = Array.isArray(claimed)
      ? claimed.filter(function (r) {
          if (isVoidedClaimedReward(r)) return false;
          if (!r || !r.redeemable || r.redeemable.usage_status !== 'not_used') return false;
          let code = getRewardCode(r);
          if (!code) return true;
          return !appliedCodes.has(code);
        })
      : [];

    if (unused.length === 0) {
      claimedContainerEl.innerHTML =
        '<div style="font-size:13px;color:#666;">No unused claimed subscription rewards.</div>';
      return;
    }

    claimedContainerEl.innerHTML =
      '<div style="margin-bottom:6px;font-weight:600;font-size:14px;">Claimed but unused rewards</div>' +
      unused
        .map(function (r) {
          let code =
            (r.redeemable && (r.redeemable.code || r.redeemable.voucher_code)) ||
            r.voucher_code ||
            r.code ||
            '';
          let codeStr = String(code).trim();
          let escapedCodeStr = escapeHtml(codeStr);
          let titleHtml = escapeHtml(r.title || r.name || 'Reward');
          return (
            '' +
            '<div style="border:1px dashed #ddd;padding:10px;margin-bottom:8px;border-radius:6px;font-size:13px;display:flex;justify-content:space-between;align-items:center;gap:12px;">' +
            '<div>' +
            '<div style="font-weight:600;">' +
            titleHtml +
            '</div>' +
            '<div style="margin-top:4px;">' +
            'Code: <code style="background:#f5f5f5;padding:2px 4px;border-radius:3px;">' +
            escapedCodeStr +
            '</code>' +
            '</div>' +
            '</div>' +
            '<button data-apply-code="' +
            escapedCodeStr +
            '" style="padding:6px 12px;border-radius:4px;border:none;cursor:pointer;background:#0d3c3a;color:#fff;white-space:nowrap;">Apply</button>' +
            '</div>'
          );
        })
        .join('');

    claimedContainerEl.querySelectorAll('button[data-apply-code]').forEach(function (btn) {
      btn.addEventListener('click', async function () {
        let code = btn.getAttribute('data-apply-code');
        if (!code) return;
        await handleApplyUnusedReward(subscriptionId, code, btn);
      });
    });
  }

  async function openRewardsForSubscription(subscriptionId) {
    if (subscriptionId === null || subscriptionId === undefined || subscriptionId === '') {
      console.error('[Loyalty] No subscription ID provided');
      return;
    }

    let numericSubscriptionId = Number(subscriptionId);
    if (!Number.isFinite(numericSubscriptionId)) {
      console.error('[Loyalty] Invalid subscription ID provided', { subscriptionId: subscriptionId });
      return;
    }

    if (isLoyaltySubscriptionsLoading) return;
    isLoyaltySubscriptionsLoading = true;

    openModal();
    selectedSubscriptionId = numericSubscriptionId;
    
    if (rewardsContainerEl) {
      rewardsContainerEl.innerHTML = 'Loading rewards…';
    }
    if (claimedContainerEl) {
      claimedContainerEl.innerHTML = '';
    }
    if (summaryEl) {
      summaryEl.textContent = '';
    }

    try {
      await showRewardsForSubscription(numericSubscriptionId);
    } catch (err) {
      console.error('[Loyalty] Failed to load rewards for subscription', err);
      if (rewardsContainerEl) {
        rewardsContainerEl.innerHTML =
          '<p>Failed to load rewards. Please try again later.</p>';
      }
      if (claimedContainerEl) {
        claimedContainerEl.innerHTML = '';
      }
    } finally {
      isLoyaltySubscriptionsLoading = false;
    }
  }

  window.etApplyRewards = function(subscriptionId) {
    if (!subscriptionId) {
      console.error('[Loyalty] etApplyRewards called without subscription ID');
      return;
    }
    openRewardsForSubscription(subscriptionId);
  };

  document.addEventListener(
    'Recharge::location::change',
    function () {
      closeModal();
      // Affinity may have written on the way (as the shared read memo assumes).
      forgetPrefetchedRewards();
    },
    true
  );

  // affinity-update-meals.js announces a usable overview once the page is
  // idle, saying whether "Apply rewards" is offered (#363).
  document.addEventListener('ET::overview::prefetch', function (event) {
    if (event && event.detail && event.detail.applyRewards) prefetchLoyaltyRewards();
  });
  document.addEventListener('Affinity:refresh', function () {
    forgetPrefetchedRewards();
  });

})();
